import React, { useState, useEffect, memo } from 'react';
import UnifiedMap from './UnifiedMap';
import { normalizeProjectGeolocation, getProjectTrailPoints } from '../utils/projectLocation';

const SidebarSection = memo(({ 
  project = null, 
  aiSuggestions = [], 
  liveTrailPoints = [], 
  bottomOverlayPercent = 0,
  messagePins = [], // Message pins from chat messages
  showMessagePins = true, // Toggle for message pins visibility
  mapZoomTarget = null // Target location to zoom to { lat, lng, zoom }
}) => {
  const [userLocation, setUserLocation] = useState(null);
  const [waypoints, setWaypoints] = useState([]);

  // Get user's current location
  useEffect(() => {
    if (!navigator.geolocation) return;
    // Fast initial fix (cached allowed)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => {},
      { enableHighAccuracy: false, timeout: 3000, maximumAge: 300000 }
    );
  }, []);

  // Helper function to calculate proper map initialization for trails
  const getMapConfig = (geolocation) => {
    const centerPoint = normalizeProjectGeolocation({ geolocation });
    const trailPoints = getProjectTrailPoints({ geolocation });

    if (trailPoints.length > 1) {
      const lats = trailPoints.map((p) => p.lat);
      const lngs = trailPoints.map((p) => p.lng);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;

      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      let zoom;
      if (maxDiff > 0.1) zoom = 10;
      else if (maxDiff > 0.05) zoom = 11;
      else if (maxDiff > 0.02) zoom = 12;
      else if (maxDiff > 0.01) zoom = 13;
      else if (maxDiff > 0.005) zoom = 14;
      else zoom = 15;

      return {
        center: [centerLat, centerLng],
        zoom,
        bounds: [[minLat, minLng], [maxLat, maxLng]],
      };
    }

    if (centerPoint) {
      return {
        center: [centerPoint.lat, centerPoint.lng],
        zoom: 14,
      };
    }

    return { center: [47.6062, -122.3321], zoom: 10 };
  };

  // Determine mode and props for UnifiedMap
  const mode = project && project.geolocation ? 'view' : 'create';
  const mapConfig = project && project.geolocation ? getMapConfig(project.geolocation) : {};

  return (
    <div className="flex flex-col h-full">
      <UnifiedMap 
        key={project ? `project-map-${project.id}` : 'create-map'}
        mode={mode}
        layoutContext="sidebar"
        // View mode props
        projects={project ? [project] : []}
        userLocation={project?.status === 'ongoing' && liveTrailPoints?.length > 0 ? { lat: liveTrailPoints[liveTrailPoints.length - 1][0], lng: liveTrailPoints[liveTrailPoints.length - 1][1] } : userLocation}
        center={mapConfig.center}
        zoom={mapConfig.zoom}
        bounds={mapConfig.bounds}
        showUserLocation={true}
        showProjectNumbers={false}
        autoCenterOnUser={!project?.geolocation} 
        autoCenterIgnoreExplicitCenter={!project?.geolocation}
        mapZoomTarget={mapZoomTarget}
        // Create mode props
        waypoints={waypoints}
        onWaypointsChange={setWaypoints}
        // AI suggestions
        aiSuggestions={aiSuggestions}
        // Message pins
        messagePins={messagePins}
        showMessagePins={showMessagePins}
        // Live trail
        liveTrailPoints={project?.status === 'ongoing' ? liveTrailPoints : []}
        bottomOverlayPercent={bottomOverlayPercent}
      />
    </div>
  );
});

SidebarSection.displayName = 'SidebarSection';

export default SidebarSection;